import React, { useState, useEffect } from 'react';
import { COLORS } from './theme';
import Nav from './Nav';
import AppView from './AppView';
import Dashboards from './hub/Dashboards.jsx';
import AdminPanel from './admin/AdminPanel.jsx';
import Team from './hub/Team.jsx';
import { useAuth, LoginScreen } from '@bstock/auth/client';

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || '';

const APP_ICONS = {
  chart: (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={COLORS.primary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" />
    </svg>
  ),
  offer: (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
    </svg>
  ),
  mail: (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" />
    </svg>
  ),
  support: (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#8b5cf6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  ),
  image: (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#ec4899" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" />
    </svg>
  )
};

function HealthDot({ status }) {
  const color = status === 'ok' ? COLORS.success
    : status === 'coming_soon' ? COLORS.textMuted
    : status === 'error' ? COLORS.danger
    : COLORS.border; // loading/unknown

  const label = status === 'ok' ? 'Online'
    : status === 'coming_soon' ? 'Coming Soon'
    : status === 'error' ? 'Offline'
    : 'Checking...';

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: COLORS.textMuted }}>
      <span style={{
        width: 8, height: 8, borderRadius: '50%', backgroundColor: color,
        display: 'inline-block',
        boxShadow: status === 'ok' ? `0 0 6px ${COLORS.success}` : 'none'
      }} />
      {label}
    </span>
  );
}

function AppCard({ app, healthStatus }) {
  const isDisabled = app.comingSoon || !app.url;

  const cardStyle = {
    background: COLORS.cardBg,
    borderRadius: 12,
    border: `1px solid ${COLORS.border}`,
    padding: 24,
    cursor: isDisabled ? 'default' : 'pointer',
    opacity: isDisabled ? 0.6 : 1,
    transition: 'all 0.2s ease',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    textDecoration: 'none',
    color: COLORS.text,
    position: 'relative'
  };

  const handleClick = () => {
    if (!isDisabled) {
      window.open(app.url, '_blank', 'noopener');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !isDisabled) {
      handleClick();
    }
  };

  return (
    <div
      style={cardStyle}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      tabIndex={isDisabled ? -1 : 0}
      role={isDisabled ? undefined : 'link'}
      onMouseEnter={(e) => {
        if (!isDisabled) {
          e.currentTarget.style.background = COLORS.cardHover;
          e.currentTarget.style.borderColor = COLORS.primary;
          e.currentTarget.style.transform = 'translateY(-2px)';
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = COLORS.cardBg;
        e.currentTarget.style.borderColor = COLORS.border;
        e.currentTarget.style.transform = 'translateY(0)';
      }}
    >
      {app.comingSoon && (
        <span style={{
          position: 'absolute', top: 12, right: 12,
          background: COLORS.accent, color: '#fff',
          fontSize: 11, fontWeight: 600, padding: '2px 8px',
          borderRadius: 4, textTransform: 'uppercase', letterSpacing: 0.5
        }}>
          Coming Soon
        </span>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        {APP_ICONS[app.icon] || null}
        <h3 style={{ fontSize: 18, fontWeight: 600 }}>{app.name}</h3>
      </div>
      <p style={{ color: COLORS.textMuted, fontSize: 14, lineHeight: 1.5, flex: 1 }}>
        {app.description}
      </p>
      <HealthDot status={healthStatus} />
    </div>
  );
}

// Hash routing, deliberately minimal (no router dependency).
//
// The TV view is the reason this exists: a wallboard has to be a URL you can
// point a screen at and leave. A dashboard reachable only by clicking through
// the portal every morning is not a wallboard, it is a page someone has to
// remember to open.
//
//   #/                    home — everything available, as cards
//   #/dashboards/<key>    a dashboard in the main view
//   #/dashboards/<key>/<tab>  one tab of a dashboard that has tabs
//   #/apps/<id>           an app in the main view
//   #/admin               the admin panel
//   #/team                a lead's own people (team profile)
//
// #/dashboards with no key still resolves (first view the nav offers), so every
// link that existed before this nav shipped still lands somewhere real.
export function parseHash() {
  const raw = (window.location.hash || '').replace(/^#\/?/, '');
  const [section, param, sub] = raw.split('/');
  if (section === 'dashboards') return { view: 'dashboards', viewKey: param || null, appId: null, tab: (param && sub) || null };
  if (section === 'apps' && param) return { view: 'app', viewKey: null, appId: param };
  if (section === 'admin') return { view: 'admin', viewKey: null, appId: null };
  if (section === 'team') return { view: 'team', viewKey: null, appId: null };
  return { view: 'home', viewKey: null, appId: null };
}

export function hashFor(route) {
  if (route.view === 'dashboards') {
    if (!route.viewKey) return '#/dashboards';
    return route.tab ? `#/dashboards/${route.viewKey}/${route.tab}` : `#/dashboards/${route.viewKey}`;
  }
  if (route.view === 'app') return `#/apps/${route.appId}`;
  if (route.view === 'admin') return '#/admin';
  if (route.view === 'team') return '#/team';
  return '#/';
}

// Keeps the address bar on the view and tab being read, without a hashchange:
// a link copied from it opens the same tab. Module-level so its identity is
// stable and Dashboards' effects do not re-run on every render of the shell.
function replaceDashboardHash(key, tab) {
  window.history.replaceState(null, '', hashFor({ view: 'dashboards', viewKey: key, tab }));
}

function Portal({ user, onLogout }) {
  const [apps, setApps] = useState([]);
  const [health, setHealth] = useState({});
  const [views, setViews] = useState([]);
  const [viewsError, setViewsError] = useState('');
  const [loading, setLoading] = useState(true);
  const [route, setRoute] = useState(parseHash);
  // What this caller may do, as the server computes it. The nav used to derive
  // admin access from the role string alone, which is why a permission granted
  // to a non-admin had no way to be reached.
  const [capabilities, setCapabilities] = useState({ isAdmin: false, canManageApps: false });
  // Null until /api/apps answers, and null forever on *.run.app. AppView treats
  // it as "not embeddable", so the pane renders as a launch point exactly as it
  // does today until the tools actually share a parent domain.
  const [sessionCookieDomain, setSessionCookieDomain] = useState(null);

  // Back/forward and a pasted link both have to work — a TV URL that only
  // resolves when typed into a fresh tab is a URL people will report as broken.
  useEffect(() => {
    const onHash = () => setRoute(parseHash());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  const navigate = (next) => { window.location.hash = hashFor(next).slice(1); };

  useEffect(() => {
    fetch('/api/apps', { credentials: 'include' })
      .then(r => r.json())
      .then(data => {
        setApps(data.apps || []);
        if (data.capabilities) setCapabilities(data.capabilities);
        setSessionCookieDomain(data.sessionCookieDomain || null);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (apps.length === 0) return;
    fetch('/api/apps/health', { credentials: 'include' })
      .then(r => r.json())
      .then(data => setHealth(data))
      .catch(() => {});
  }, [apps]);

  // The views list is fetched HERE, not only inside Dashboards, because the nav
  // needs it and because the shell needs each view's displayMode to decide
  // whether to render any chrome at all (see isWallboard below).
  // Whether this person leads anyone. The page itself refuses a non-lead too;
  // this only decides whether the nav offers it.
  const [hasReports, setHasReports] = useState(false);
  useEffect(() => {
    fetch('/api/hub/team/access', { credentials: 'include' })
      .then(r => (r.ok ? r.json() : { hasReports: false }))
      .then(d => setHasReports(!!d.hasReports))
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetch('/api/hub/views', { credentials: 'include' })
      .then(r => (r.ok ? r.json() : r.json().then(e => Promise.reject(new Error(e.error)))))
      .then(d => setViews(d.views || []))
      .catch(e => setViewsError(e.message || 'Dashboards unavailable'));
  }, []);

  // "May reach the Admin panel", which is no longer the same question as "is
  // an admin": someone holding apps:write edits the registry there and nothing
  // else. The server decides both halves; this only renders them.
  const isAdmin = capabilities.isAdmin || user.role === 'admin' || capabilities.canManageApps;

  // Resolve the dashboard key the nav should highlight. A deep link to a view
  // that does not exist (stale bookmark, revoked role) falls back to the first
  // one rather than highlighting nothing.
  const activeViewKey =
    route.view === 'dashboards'
      ? (views.some(v => v.key === route.viewKey) ? route.viewKey : (views[0]?.key || null))
      : null;

  // A wallboard gets NO nav and NO header. This is read from the view's own
  // displayMode rather than from a hardcoded 'tv' key, so a second wallboard
  // created later is chromeless without another code change.
  const isWallboard =
    route.view === 'dashboards' &&
    views.find(v => v.key === activeViewKey)?.displayMode === 'wallboard';

  if (isWallboard) {
    return (
      <Dashboards
        viewKey={activeViewKey}
        views={views}
        initialTab={route.tab}
        onViewChange={replaceDashboardHash}
      />
    );
  }

  const activeApp = route.view === 'app' ? apps.find(a => a.id === route.appId) : null;
  const heading =
    route.view === 'admin' ? 'Admin'
      : route.view === 'team' ? 'My team'
      : route.view === 'app' ? (activeApp?.name || 'App')
      : route.view === 'dashboards' ? (views.find(v => v.key === activeViewKey)?.name || 'Dashboards')
      : 'Home';

  return (
    <div style={{ display: 'flex', minHeight: '100vh', color: COLORS.text }}>
      <Nav
        user={user}
        apps={apps}
        health={health}
        views={views}
        viewsError={viewsError}
        route={{ ...route, viewKey: activeViewKey }}
        onNavigate={navigate}
        onLogout={onLogout}
        isAdmin={isAdmin}
        hasReports={hasReports}
      />

      {/* This column owns the viewport height so an embedded app can fill what
          is left after the header. Previously it was sized by stretch and the
          pane asked for height:100%, which resolved to the FULL column — header
          included — pushing the iframe's last ~53px below the fold and giving
          the portal its own scrollbar nested outside the app's. */}
      <div style={{
        flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column',
        height: '100vh', minHeight: 0
      }}>
        <header style={{
          flex: '0 0 auto',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '16px 32px',
          borderBottom: `1px solid ${COLORS.border}`,
          background: 'rgba(15, 23, 42, 0.8)',
          backdropFilter: 'blur(8px)'
        }}>
          <h1 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>{heading}</h1>
        </header>

        {/* The content region scrolls, not the page — the column above is a fixed
            100vh so an embedded app can fill it exactly, which means the page
            itself no longer grows and everything else in here (dashboards, the
            admin panel, a long app grid) needs somewhere to scroll.

            Deliberately NOT a flex container. It was one, and a child taller
            than the region got SHRUNK to fit instead of scrolling, because flex
            children shrink by default — a long dashboard would have been
            compressed and clipped rather than scrollable. Normal block flow
            lets content grow to its natural height and this box scroll it.
            `minHeight: 0` is what allows this flex child to be smaller than its
            own content in the first place. */}
        <div style={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }}>
        {route.view === 'admin' ? (
          <AdminPanel onBack={() => navigate({ view: 'home' })} />
        ) : route.view === 'team' ? (
          <Team />
        ) : route.view === 'dashboards' ? (
          <Dashboards
            viewKey={activeViewKey}
            views={views}
            initialTab={route.tab}
            onViewChange={replaceDashboardHash}
          />
        ) : route.view === 'app' ? (
          <AppView
            app={activeApp}
            health={health[route.appId]}
            sessionCookieDomain={sessionCookieDomain}
            portalHost={typeof window !== 'undefined' ? window.location.hostname : null}
          />
        ) : (
          <main style={{ maxWidth: 960, padding: '40px 32px' }}>
            {loading ? (
              <p style={{ color: COLORS.textMuted, marginTop: 40 }}>Loading apps...</p>
            ) : apps.length === 0 ? (
              <p style={{ color: COLORS.textMuted, marginTop: 40 }}>
                No apps available for your role.
              </p>
            ) : (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                gap: 20
              }}>
                {apps.map(app => (
                  <AppCard
                    key={app.id}
                    app={app}
                    healthStatus={health[app.id] || null}
                  />
                ))}
              </div>
            )}
          </main>
        )}
        </div>
      </div>
    </div>
  );
}

export default function App() {
  const { user, loading, error, login, logout } = useAuth();

  if (loading) {
    return (
      <div style={{
        display: 'flex', justifyContent: 'center', alignItems: 'center',
        minHeight: '100vh', color: COLORS.textMuted
      }}>
        Loading...
      </div>
    );
  }

  if (!user) {
    return (
      <LoginScreen
        googleClientId={GOOGLE_CLIENT_ID}
        onCredential={login}
        appName="B-Stock Internal Tools"
        subtitle="Access your team's internal applications"
        error={error}
      />
    );
  }

  return <Portal user={user} onLogout={logout} />;
}
